<template>
    <div>
        <form
            class="flex bg-panel border max-md:container py-2 md:py-3 px-4 md:rounded-lg mb-5"
            @submit.prevent="onSearchPhraseChange"
        >
            <InputField
                v-model="phrase"
                :placeholder="t('search_courses_tab.placeholder')"
                icon="icon-search"
                clickable-icon
                class="search-field w-full md:max-w-80 mr-auto"
                @click-icon="onSearchPhraseChange"
            />

            <BtnMenu
                variant="outlined"
                :label="sortingLabels[sortBy]"
                :options="sortingOptions"
                icon="sorting"
                size="lg"
                class="ml-3 whitespace-nowrap"
            />
        </form>

        <Spinner
            v-if="searchInProgress"
            class="my-32"
            appearance="dots"
            size="xl"
            color="black"
        ></Spinner>

        <template v-else>
            <EmptySearchResult v-if="coursesTotalCount === 0" />

            <div
                v-if="coursesTotalCount > 0"
                class="space-y-2 mb-5 max-md:container"
            >
                <CardCourse
                    v-for="course in coursesList"
                    :key="course.id"
                    v-memo="[maxSM]"
                    :course-name="course.name"
                    :course-id="course.id"
                    title-tag="h2"
                    :num-posts="course.number_questions"
                    :num-documents="course.documents"
                    :num-users="course.students"
                    :mode="maxSM ? 'stack' : 'narrow'"
                    no-university-name
                ></CardCourse>
                <Paginate
                    v-if="coursesLastPage > 1"
                    v-model="currentPage"
                    :page-count="coursesLastPage"
                    :page-range="6"
                    :margin-pages="0"
                    :click-handler="onPageChange"
                    hide-prev-next
                    first-last-button
                    :first-button-text="firstPage"
                    :last-button-text="lastPage"
                    break-view-class="hidden"
                    prev-text='<div class="flex cursor-pointer"><span class="mr-2 icon icon-chevron-left"></span></div>'
                    next-text='<div class="flex cursor-pointer"><span class="ml-2 icon icon-chevron-right"></span></div>'
                    break-view-link-class="cursor-default text-black"
                    container-class="flex items-center justify-center w-full text-primary"
                    active-class="text-black cursor-default"
                    page-class="mx-2 text-lg cursor-pointer"
                    class="!mt-5"
                />
            </div>
        </template>
    </div>
</template>

<script>
import { ref } from 'vue';
import { mapGetters } from 'vuex';
import { isEqual } from 'lodash-es';
import Paginate from 'vuejs-paginate-next';
import EmptySearchResult from '@/components/EmptySearchResult';
import CardCourse from '@/components/CardCourse';
import { useScreenSize } from '@/composables/screenSize';
import { useTranslation } from 'i18next-vue';

export default {
    name: 'UniversityShowCourses',
    components: {
        EmptySearchResult,
        CardCourse,
        Paginate,
    },
    setup() {
        const { t } = useTranslation('university');
        return {
            phrase: ref(''),
            maxSM: useScreenSize().maxSM,
            t,
        };
    },
    computed: {
        ...mapGetters({
            university: 'university/currentUniversity',
            params: 'university/searchParams',
            searchInProgress: 'university/coursesProgress',
            coursesList: 'university/coursesList',
            coursesTotalCount: 'university/coursesTotalCount',
            coursesLastPage: 'university/coursesLastPage',
            locale: 'ui/getLocale',
        }),
        currentPage() {
            return parseInt(this.params.p || 1, 10);
        },
        sortBy() {
            return this.params.sort_type_courses || 'students';
        },
        firstPage() {
            let hideGoToFirst = '';
            if (this.currentPage === 1) {
                hideGoToFirst = 'hidden';
            }

            return (
                `<span class="flex ${hideGoToFirst}">` +
                `<i class="icon icon icon-chevron-left -mr-4"></i>` +
                `<i class="icon icon icon-chevron-left"></i>` +
                `</span>`
            );
        },
        lastPage() {
            let hideGoToLast = '';
            if (this.currentPage === this.coursesLastPage) {
                hideGoToLast = 'hidden';
            }

            return (
                `<span class="flex ${hideGoToLast}">` +
                `<i class="icon icon icon-chevron-right"></i>` +
                `<i class="icon icon icon-chevron-right -ml-4"></i>` +
                `</span>`
            );
        },
        sortingLabels() {
            return {
                relevance: this.$t(`sorting.best_match`),
                students: this.$t(`sorting.most_students`),
                documents: this.$t(`sorting.most_documents`),
                number_questions: this.$t(`sorting.most_posts`),
            };
        },
        sortingOptions() {
            return Object.entries(this.sortingLabels).map(([key, label]) => ({
                label,
                onClick: () => this.onSortChange(key),
            }));
        },
    },
    watch: {
        params: {
            deep: true,
            immediate: true,
            handler: function paramsWatcher(params, oldParams) {
                if (params.content === 'courses' && !isEqual(params, oldParams)) {
                    this.getCourses();

                    this.phrase = params.phrase || this.phrase;
                }
            },
        },
    },
    methods: {
        getCourses() {
            this.$store.dispatch('university/retrieveCourses', {
                p: this.currentPage,
                sort_type_courses: this.sortBy,
                ...this.params,
                content: 'courses',
                university_id: this.university.id,
            });
        },
        onSearchPhraseChange() {
            return this.$store.dispatch('university/applyParams', {
                phrase: this.phrase,
                p: 1,
            });
        },
        onPageChange(page) {
            this.$store.dispatch('university/applyParams', {
                p: page,
            });
        },
        onSortChange(sort) {
            this.$store.dispatch('university/applyParams', {
                sort_type_courses: sort,
                p: 1,
            });
        },
    },
};
</script>
